import React from 'react';
import { maxWidth } from './themeConfig';
import colors from './colors';
import CloseIcon from './icons/CloseIcon';
/**
 * Component style overrides for Blue Shield of California
 */
const sharedFormControlStyles = {
  root: {
    marginTop: 6,
    padding: '8px 12px',
    alignItems: 'flex-start',
    backgroundColor: `${colors.states.transparent} !important`,
    transition: 'none',
    '&:hover': {
      backgroundColor: `${colors.states.transparent} !important`,
    },
    '&$checked': {
      backgroundColor: `${colors.states.transparent} !important`,
    },
    '&:focus': {
      outline: 'none !important',
      border: 'none !important',
      boxShadow: 'none !important',
      backgroundColor: `${colors.states.transparent} !important`,
    },
    '&.Mui-focusVisible': {
      outline: 'none !important',
      border: 'none !important',
      boxShadow: 'none !important',
      backgroundColor: `${colors.states.transparent} !important`,
    },
    '&:focus-within': {
      outline: 'none !important',
      border: 'none !important',
      boxShadow: 'none !important',
      backgroundColor: `${colors.states.transparent} !important`,
    },
  },
};

const sharedInlineStyles = {
  color: `${colors.border.unselected} !important`,
  '&.Mui-checked': {
    color: `${colors.primary.main} !important`,
  },
};

const componentStyles = {
  Survey: {
    showSourceOnSurvey: true,
    rootStyle: {
      marginTop: 0,
      zIndex: 99,
      padding: '0 !important',
    },
    surveyWrapper: {
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    },
    disclaimer: {
      maxWidth: 780,
      '@media (max-width: 1140px)': { maxWidth: '800px' },
    },
    attribution: {
      padding: '0 !important',
    },
    attributionContent: {
      background: 'white',
      borderRadius: 8,
      padding: 20,
    },
    root: {
      maxWidth,
    },
  },
  Questions: {
    rootStyle: {
      border: 'none',
    },
    pageData: {
      maxWidth,
      padding: '20px 0',
    },
    singleQuestionContainer: {
      padding: '20px 0',
    },
    instructionWrapper: {
      padding: '16px 0',
      marginBottom: 0,
    },
    subtitle: {
      fontSize: '16px',
    },
    radioGroup: {
      margin: '15px 0 8px 6px',
    },
  },
  RadioButton: {
    root: {
      padding: '0 4px 0 0',
      display: 'flex',
      alignItems: 'flex-start',
      '& svg': {
        width: '0.9em',
        height: '0.9em',
      },
      '&:hover': {
        backgroundColor: `${colors.states.transparent} !important`,
      },
      '&:focus': {
        outline: 'none !important',
        border: 'none !important',
        boxShadow: 'none !important',
      },
      '&.Mui-focusVisible': {
        outline: 'none !important',
        border: 'none !important',
        boxShadow: 'none !important',
      },
    },
    inline: sharedInlineStyles,
  },
  Checkbox: {
    root: {
      padding: '0 4px',
      display: 'flex',
      alignItems: 'flex-start',
      '& svg': {
        width: '0.9em',
        height: '0.9em',
      },
      '&:hover': {
        backgroundColor: `${colors.states.transparent} !important`,
      },
      '&:focus': {
        outline: 'none !important',
        border: 'none !important',
        boxShadow: 'none !important',
      },
      '&.Mui-focusVisible': {
        outline: 'none !important',
        border: 'none !important',
        boxShadow: 'none !important',
      },
    },
    inline: sharedInlineStyles,
  },
  FormControlOption: {
    ...sharedFormControlStyles,
    label: {
      backgroundColor: `${colors.states.transparent} !important`,
      '&$checked': {
        backgroundColor: `${colors.states.transparent} !important`,
      },
      '&:focus': {
        outline: 'none !important',
        border: 'none !important',
        boxShadow: 'none !important',
      },
    },
  },
  Error: {
    noPulsatingError: true,
  },
  SingleQuestion: {
    wrapper: {
      backgroundColor: colors.background.lightBlue,
    },
  },
  QuestionMatrix: {
    matrix: {
      maxWidth,
      tableLayout: 'fixed',
      overflow: 'scroll',
    },
    headerContainerCellStyle: {
      backgroundColor: colors.background.matrixHeader,
    },
    matrixStyle: {
      '& tbody tr:nth-child(even)': {
        backgroundColor: `${colors.background.lightGray} !important`,
      },
      '& tbody tr th': {
        padding: '0 10px',
      },
    },
  },
  PanelQuestion: {
    wrapperStyle: {
      backgroundColor: colors.text.tertiary,
    },
    container: {
      padding: '12px 0',
    },
    title: {
      padding: '20px 0',
    },
  },
  Summary: {
    link: {
      textDecoration: 'underline',
      color: colors.primary.main,
    },
    block: {
      backgroundColor: colors.background.lightGray,
    },
    blockTitle: {
      fontSize: 17,
      fontWeight: 600,
      lineHeight: '20.92px',
      letterSpacing: '0.15px',
      verticalAlign: 'middle',
    },
  },
  ContentBlock: {
    colorOverrides: {
      'Light Gray': {
        backgroundColor: colors.background.lightGray,
        color: '#4b4b4b',
      },
      'CM Blue': {
        backgroundColor: colors.primary.main,
        color: '#FFFFFF',
        linkColor: '#FFFFFF',
      },
    },
    toSentenceCase: true,
  },
  LeavingAppDialog: {
    closeIcon: <CloseIcon />,
    paper: {
      backgroundColor: colors.text.tertiary,
      borderRadius: '16px !important',
      boxShadow:
        '0px 20px 40px rgba(34, 10, 51, 0.2), 0px 0px 15px rgba(34, 10, 51, 0.05)',
    },
    header: {
      '@media (max-width: 800px)': {
        padding: '12px 13px 0px 0px',
      },
    },
    contentRoot: {
      padding: '10px 48px 48px',
      '@media (max-width: 800px)': {
        padding: '10px 20px 40px 20px',
      },
      '& h4': {
        '@media (max-width: 800px)': {
          padding: 0,
          fontSize: '24px',
          lineHeight: '120%',
        },
      },
    },
    buttonSpace: {
      '@media (max-width: 800px)': {
        alignItems: 'center',
      },
    },
    button: {
      width: 'auto',
      minWidth: 170,
      height: 56,
      backgroundColor: colors.primary.main,
      borderRadius: 32,
      padding: '16px 32px',
      fontWeight: 600,
      fontFamily: 'Fieldwork Geo',
      lineHeight: '24px',
      fontSize: '18px',
      marginTop: 28,
      '@media (max-width: 800px)': {
        marginTop: 24,
        width: 'auto',
      },
      '&:hover': {
        backgroundColor: colors.background.default,
      },
    },
    secondaryButton: {
      backgroundColor: 'transparent',
      color: colors.primary.main,
      textTransform: 'none',
      fontWeight: 600,
      lineHeight: '24px',
      fontSize: '18px',
      fontFamily: 'Fieldwork Geo',
      border: 'none !important',
      '&:hover': {
        backgroundColor: 'transparent',
        color: colors.background.default,
      },
      '@media (max-width: 800px)': {
        marginTop: 24,
      },
    },
    closeButton: {
      '&:hover': {
        backgroundColor: 'transparent',
      },
    },
    headerText: {
      color: colors.text.primary,
      fontWeight: 600,
      fontSize: '32px',
      lineHeight: '38px',
      letterSpacing: '0px',
      paragprahSpacing: '24px',
      '@media (max-width: 768px)': {
        fontSize: '24px',
        lineHeight: '38px',
      },
    },
  },
  ActionButtons: {
    toSentenceCase: true,
    fullWidth: true,
    buttonsWrapper: {
      justifyContent: 'space-between',
      width: '100%',
      marginBottom: 32,
    },
    container: {
      justifyContent: 'flex-start',
      padding: '20px 0',
    },
    backButton: {
      withIcon: true,
      border: 'none',
      fontWeight: 600,
      fontSize: 18,
      lineHeight: '20px',
      color: colors.primary.main,
      height: '56px',
      order: 1,
      paddingLeft: 0,
      justifyContent: 'flex-start',
      '& .backIcon, & img': {
        transition: 'transform 0.2s ease-in-out, filter 0.2s ease-in-out',
      },
      '&:hover': {
        backgroundColor: 'transparent',
        color: colors.background.default,
        '&:hover .backIcon, &:hover img': {
          transform: 'translateX(-6px)',
          filter:
            'brightness(0) saturate(100%) invert(16%) sepia(99%) saturate(1651%) hue-rotate(203deg) brightness(89%) contrast(108%)',
        },
      },
    },
    secondaryAction: {
      border: 'none',
      fontWeight: 600,
      fontSize: 18,
      padding: 0,
      maxWidth: 217,
      textWrap: 'auto',
      textAlign: 'start',
      lineHeight: '18.5px',
      color: colors.primary.main,
      height: '56px',
      '&:hover': {
        backgroundColor: 'transparent',
      },
      order: 2,
      '@media (max-width: 768px)': {
        maxWidth: 'none',
      },
    },
    primaryAction: {
      borderRadius: '32px',
      backgroundColor: colors.primary.main,
      fontWeight: 600,
      fontSize: 18,
      lineHeight: '24px',
      height: '56px',
      padding: '16px 32px',
      order: 3,
      minWidth: 'auto',
      '&:hover': {
        backgroundColor: colors.background.default,
      },
    },
  },
  WasThisHelpful: {
    hideButtonIcon: true,
    toSentenceCase: true,
    hideCMLogo: true,
    showProgress: false,
    alwaysShowButtons: false,
    root: {
      padding: '32px !important',
    },
    title: {
      fontWeight: 600,
      fontSize: 20,
      lineHeight: '24px',
      letterSpacing: '0px',
    },
    btnWrapper: {
      justifyContent: 'flex-start',
      gap: 8,
      padding: '16px 0',
    },
    dontShowAgain: {
      fontSize: '14px',
      color: colors.primary.main,
      lineHeight: '20px',
      textDecoration: 'underline',
      fontWeight: 300,
      paddingTop: '16px',
      cursor: 'pointer',
      fontFamily: 'Fieldwork Geo',
      '&:hover': {
        color: colors.primary.default,
      },
    },
    btn: {
      borderRadius: '32px',
      height: '40px',
      width: '75px',
      fontSize: 18,
      lineHeight: '24px',
      fontWeight: 600,
      color: colors.text.tertiary,
      backgroundColor: colors.primary.main,
      '&:hover': {
        backgroundColor: colors.background.default,
      },
    },
    noBtn: {
      border: `1px solid ${colors.primary.main}`,
      width: '70px',
      backgroundColor: 'transparent',
      color: colors.primary.main,
      '&:hover': {
        backgroundColor: 'transparent',
        border: `1px solid ${colors.primary.default}`,
        color: colors.primary.default,
      },
    },
    optionTitle: {
      fontSize: 20,
      fontWeight: 600,
      lineHeight: '24px',
      marginBottom: 8,
    },
    optionBtn: {
      border: 'none',
      padding: '12px 0',
      fontSize: 18,
      fontWeight: 300,
      lineHeight: '24px',
      marginBottom: 0,
      '& img': {
        marginRight: 16,
      },
    },
    optionBtnWrapper: {
      justifyContent: 'flex-start',
    },
    questionSubmitBtn: {
      backgroundColor: colors.primary.main,
      borderRadius: '32px',
      height: '40px',
      width: '100px',
      fontWeight: 600,
      fontSize: 18,
      lineHeight: '24px',
      letterSpacing: 0,
      marginTop: 16,
      '&:hover': {
        backgroundColor: colors.background.default,
      },
    },
    successTitle: {
      color: colors.text.primary,
      fontSize: 20,
      lineHeight: '24px',
      letterSpacing: '0.15px',
      fontWeight: 600,
      padding: '24px 0',
    },
  },
  Disclaimer: {
    isLeftAlign: true,
    root: {
      padding: '0 !important',
      marginTop: 0,
      '@media (max-width: 1140px)': { padding: '0px 10px 0 !important' },
    },
    content: {
      boxShadow: 'none',
      backgroundColor: 'transparent !important',
      '& div': {
        backgroundColor: 'transparent !important',
      },
      '& p': {
        display: 'inherit',
        margin: 0,
      },
    },
  },
  Footer: {
    isLeftAlign: true,
    embedded: {
      backgroundColor: 'transparent',
    },
    root: {
      backgroundColor: 'transparent',
      paddingTop: 0,
    },
    mt0: {
      '& p': {
        marginBottom: '1em',
      },
    },
  },
};

// Function to get component styles with pathname-based conditions
const getComponentStyles = (pathname = '') => {
  // Check if pathname matches /assessments/*/results pattern
  const isResultsPage = /^\/assessments\/[^/]+\/results/.test(pathname);

  return {
    ...componentStyles,
    Footer: {
      ...componentStyles.Footer,
      root: {
        ...componentStyles.Footer.root,
        paddingTop: isResultsPage ? 24 : 0,
      },
      wrapper: {
        maxWidth: isResultsPage ? '100%' : 800,
      },
      textWrapper: {
        marginLeft: isResultsPage ? 0 : 10,
        marginRight: isResultsPage ? 0 : 10,
        '@media (max-width: 1140px)': {
          marginLeft: 10,
          marginRight: 10,
        },
      },
    },
  };
};

export default componentStyles;
export { getComponentStyles };
